import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Divider,
  Menu,
  MenuItem,
  Badge,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  tableCellClasses,
  Checkbox, ListItemText, ListItem, List as MUIList 
} from '@mui/material';
import {
  Search,
  Add,
  Refresh,
  FilterList,
  Edit,
  Delete,
  Visibility,
  CheckCircle,
  Cancel,
  MoreVert,
  Person,
  Store,
  Phone,
  Email,
  AttachMoney,
  ShoppingCart,
  MonetizationOn,
  Description,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  Download,
  Upload,
  Link as LinkIcon,
  Image,
  CalendarToday,
  Save,
  Close,
  List
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue, green, red, grey, orange } from '@mui/material/colors';
import pb from './pocketbase';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    cursor: 'pointer',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const SellerAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: blue[500],
  boxShadow: theme.shadows[2],
}));

const StatusChip = styled(Chip)(({ verified, theme }) => ({
  fontWeight: 600,
  ...(verified && {
    backgroundColor: green[100],
    color: green[800],
    '& .MuiChip-icon': {
      color: green[800],
    },
  }),
  ...(!verified && {
    backgroundColor: orange[100],
    color: orange[800],
    '& .MuiChip-icon': {
      color: orange[800],
    },
  }),
}));

const DetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: theme.palette.background.paper,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  overflow: 'hidden',
}));

const ShopBanner = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 200,
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  position: 'relative',
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.3)',
    zIndex: 1,
  },
}));

const ShopLogoWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  bottom: 40,
  left: 20,
  zIndex: 10,
}));

const ShopLogo = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
}));

const StyledImageUpload = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}10`,
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StatIconWrapper = styled(Box)(({ theme, color }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: color ? `${color}15` : `${theme.palette.primary.main}15`,
  color: color || theme.palette.primary.main,
}));

const Sellers = () => {
  // States
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSellers, setTotalSellers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [tabValue, setTabValue] = useState(0);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [currentSellerId, setCurrentSellerId] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all', // all, verified, pending
    sortBy: 'created_desc' // created_desc, created_asc, sales_desc, sales_asc
  });
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    shop_name: '',
    shop_description: '',
    social: '',
    admin_verified: false,
    // Other fields to be updated
  });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [bannerPreview, setBannerPreview] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Current UTC time
  const [currentDateTime] = useState('2025-04-02 03:18:00');
  const [currentUser] = useState('MdAbuNafisNiloy');


  // Add these state variables
const [exportDialogOpen, setExportDialogOpen] = useState(false);
const [columnsToExport, setColumnsToExport] = useState({
  seller: true,
  shop_name: true,
  contact: true,
  status: true,
  sales: true,
  orders: true,
  joined: true,
});
const [selectedRows, setSelectedRows] = useState([]);
const [selectAllRows, setSelectAllRows] = useState(true);

// Export function that handles exporting data
const exportData = () => {
  // Filter rows if not all selected
  const rowsToExport = selectAllRows ? sellers : sellers.filter((seller, index) => 
    selectedRows.includes(index)
  );
  
  // Create headers row based on selected columns
  const headers = [];
  if (columnsToExport.seller) headers.push('Seller Name');
  if (columnsToExport.shop_name) headers.push('Shop Name');
  if (columnsToExport.contact) headers.push('Email', 'Phone');
  if (columnsToExport.status) headers.push('Verification Status');
  if (columnsToExport.sales) headers.push('Total Sales');
  if (columnsToExport.orders) headers.push('Orders');
  if (columnsToExport.joined) headers.push('Joined Date');

  // Create CSV content
  let csvContent = headers.join(',') + '\n';
  
  // Add data rows
  rowsToExport.forEach(seller => {
    const row = [];
    if (columnsToExport.seller) row.push(`"${seller.full_name}"`);
    if (columnsToExport.shop_name) row.push(`"${seller.shop_name}"`);
    if (columnsToExport.contact) {
      row.push(`"${seller.email}"`);
      row.push(`"${seller.phone}"`);
    }
    if (columnsToExport.status) row.push(`"${seller.admin_verified ? 'Verified' : 'Pending'}"`);
    if (columnsToExport.sales) row.push(`"${seller.total_sale}"`);
    if (columnsToExport.orders) row.push(`"${seller.total_orders}"`);
    if (columnsToExport.joined) row.push(`"${formatDate(seller.created)}"`);
    
    csvContent += row.join(',') + '\n';
  });
  
  // Create a blob and download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `sellers_export_${new Date().toISOString().slice(0,10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Close the dialog
  setExportDialogOpen(false);
  
  // Show success message
  setSnackbar({
    open: true,
    message: 'Sellers data exported successfully',
    severity: 'success'
  });
};

// Toggle all rows selection
const handleToggleAllRows = () => {
  setSelectAllRows(!selectAllRows);
};

// Toggle individual row selection
const handleToggleRow = (index) => {
  setSelectedRows(prevSelected => {
    if (prevSelected.includes(index)) {
      return prevSelected.filter(idx => idx !== index);
    } else {
      return [...prevSelected, index];
    }
  });
};

// Toggle column selection
const handleToggleColumn = (column) => {
  setColumnsToExport(prev => ({
    ...prev,
    [column]: !prev[column]
  }));
};

// Handle export button click
const handleExportClick = () => {
  setExportDialogOpen(true);
};



  // Fetch sellers on component mount and when filters/pagination change
  useEffect(() => {
    fetchSellers();
  }, [page, rowsPerPage, filters, searchQuery]);

  // Function to fetch sellers from the API
  const fetchSellers = async () => {
    try {
      setLoading(true);
      
      // Building filter string for PocketBase
      let filterStr = '';
      
      // Status filter
      if (filters.status === 'verified') {
        filterStr = 'admin_verified = true';
      } else if (filters.status === 'pending') {
        filterStr = 'admin_verified = false';
      }
      
      // Search query filter
      if (searchQuery) {
        const searchFilter = `(shop_name~"${searchQuery}" || full_name~"${searchQuery}" || email~"${searchQuery}")`;
        filterStr = filterStr ? `${filterStr} && ${searchFilter}` : searchFilter;
      }
      
      // Sort options
      let sortStr;
      switch (filters.sortBy) {
        case 'created_asc':
          sortStr = 'created';
          break;
        case 'sales_desc':
          sortStr = '-total_sale';
          break;
        case 'sales_asc':
          sortStr = 'total_sale';
          break;
        case 'created_desc':
        default:
          sortStr = '-created';
      }
  
      const mockSellers = await pb.collection('sellers').getList(page + 1, rowsPerPage, {
        filter: filterStr,
        sort: sortStr,
        expand: 'products,categories' // expand any relations if needed
      });
       console.log(mockSellers);
     
      mockSellers.items.forEach((seller) => {
        if(!seller.email){
            // set email visibility to true 
            pb.collection('sellers').update(seller.id, {
                emailVisibility: true
                });
            }
        })
      

      // Filter based on search query and status
      let filteredSellers = [...mockSellers.items];
      
      if (searchQuery) {
        filteredSellers = filteredSellers.filter(seller => 
          seller.shop_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          seller.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          seller.email.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      if (filters.status === 'verified') {
        filteredSellers = filteredSellers.filter(seller => seller.admin_verified);
      } else if (filters.status === 'pending') {
        filteredSellers = filteredSellers.filter(seller => !seller.admin_verified);
      }
      
      // Sort sellers
      filteredSellers.sort((a, b) => {
        switch (filters.sortBy) {
          case 'created_asc':
            return new Date(a.created) - new Date(b.created);
          case 'sales_desc':
            return b.total_sale - a.total_sale;
          case 'sales_asc':
            return a.total_sale - b.total_sale;
          case 'created_desc':
          default:
            return new Date(b.created) - new Date(a.created);
        }
      });
      
      setTotalSellers(filteredSellers.length);
      
      // Paginate results
      const paginatedSellers = filteredSellers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
      
      setSellers(paginatedSellers);
    } catch (err) {
      console.error('Error fetching sellers:', err);
      setError('Failed to load sellers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle row click to view seller details
  const handleRowClick = (seller) => {
    setSelectedSeller(seller);
    setViewModalOpen(true);
  };

  // Handle editing seller
  const handleEditClick = (event, seller) => {
    event.stopPropagation();
    setSelectedSeller(seller);
    setEditForm({
      full_name: seller.full_name,
      email: seller.email,
      phone: seller.phone,
      shop_name: seller.shop_name,
      shop_description: seller.shop_description,
      social: seller.social,
      admin_verified: seller.admin_verified,
    });
    setLogoPreview(pb.files.getUrl(seller, seller.shop_logo, { thumb: '100x100' })); // Mock image URL
    setBannerPreview(pb.files.getUrl(seller, seller.shop_banner, { thumb: '800x200' })); // Mock image URL
    setEditModalOpen(true);
  };

  // Handle action menu
  const handleMenuOpen = (event, sellerId) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setCurrentSellerId(sellerId);
  };

  const handleMenuClose = () => {
    setActionMenuAnchorEl(null);
    setCurrentSellerId(null);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: name === 'admin_verified' ? checked : value
    }));
  };

  // Handle file uploads
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setFormSubmitting(true);
  // Create a FormData object to handle file uploads
  const formData = new FormData();
    
  // Add all form fields to FormData
  Object.keys(editForm).forEach(key => {
    formData.append(key, editForm[key]);
  });
   // Add files if they exist
   if (logoFile) {
    formData.append('shop_logo', logoFile);
  }
  
  if (bannerFile) {
    formData.append('shop_banner', bannerFile);
  }
      const updatedSeller = await pb.collection('sellers').update(selectedSeller.id, formData);
        console.log('Seller updated:', updatedSeller);
      
      // In a real app, you would update the seller record in PocketBase
      // For this example, just simulate a successful update
      setTimeout(() => {
        // Update the sellers list with the edited seller
        setSellers(sellers.map(seller => 
          seller.id === selectedSeller.id 
            ? { ...seller, ...editForm } 
            : seller
        ));
        
        setSnackbar({
          open: true,
          message: 'Seller information updated successfully',
          severity: 'success'
        });
        
        setEditModalOpen(false);
        setFormSubmitting(false);
        
        // Also update the selected seller if view modal is open
        if (viewModalOpen) {
          setSelectedSeller({ ...selectedSeller, ...editForm });
        }
      }, 1000);
    } catch (error) {
      console.error('Error updating seller:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update seller information',
        severity: 'error'
      });
      setFormSubmitting(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle filter menu
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0);
    handleFilterClose();
  };

  // Handle tab change in view/edit modal
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Verify/unverify seller
  const handleToggleVerification = async (sellerId, currentStatus) => {
    try {
      // In a real app, update the seller in PocketBase
      await pb.collection('sellers').update(sellerId, {
        admin_verified: !currentStatus
        });
      // For this example, update the local state
      setSellers(sellers.map(seller => 
        seller.id === sellerId 
          ? { ...seller, admin_verified: !currentStatus } 
          : seller
      ));
      
      setSnackbar({
        open: true,
        message: currentStatus 
          ? 'Seller verification removed' 
          : 'Seller verified successfully',
        severity: 'success'
      });
      
      handleMenuClose();
    } catch (error) {
      console.error('Error updating seller verification:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update seller verification status',
        severity: 'error'
      });
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BDT',
    }).format(amount);
  };

  // Get logo URL (in a real app, would use PocketBase file URL)
  const getLogoUrl = (seller) => {
    // Check if seller has a logo file
    if (seller.shop_logo) {
      // Get the logo URL from PocketBase
      return pb.files.getUrl(seller, seller.shop_logo, {
        thumb: '100x100' // Optional thumbnail size
      });
    }
    
    // Fallback to default avatar if no logo
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(seller.shop_name)}&background=random`;
  };
  
  const getBannerUrl = (seller) => {
    // Check if seller has a banner file
    if (seller.shop_banner) {
      // Get the banner URL from PocketBase
      return pb.files.getUrl(seller, seller.shop_banner, {
        thumb: '800x200' // Optional thumbnail size
      });
    }
    
    // Fallback to a placeholder banner
    return 'https://via.placeholder.com/800x200?text=Shop+Banner';
  };
  // Format date from the provided timestamp
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Sellers Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all marketplace sellers, review their information and control verification status
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Add New Seller
        </Button> */}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <StatIconWrapper color={blue[500]}>
              <Store fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {totalSellers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Sellers
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <StatIconWrapper color={green[500]}>
              <CheckCircle fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {sellers.filter(s => s.admin_verified).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verified Sellers
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <StatIconWrapper color={orange[500]}>
              <Cancel fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {sellers.filter(s => !s.admin_verified).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Verification
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <StatIconWrapper color={grey[700]}>
              <AttachMoney fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {formatCurrency(sellers.reduce((sum, seller) => sum + seller.total_sale, 0))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Sales
              </Typography>
            </Box>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters and search */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by seller name, shop name or email..."
              variant="outlined"
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleFilterClick}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ status: 'all', sortBy: 'created_desc' });
                  fetchSellers();
                }}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Reset
              </Button>
              <Button
  variant="outlined"
  startIcon={<Download />}
  size="small"
  sx={{ textTransform: 'none' }}
  onClick={handleExportClick}
>
  Export
</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          elevation: 2,
          sx: { width: 240, maxWidth: '100%', borderRadius: 2 }
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2" fontWeight={600}>
            Filter by Status
          </Typography>
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'all'} 
          onClick={() => handleFilterChange('status', 'all')}
        >
          All Sellers
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'verified'} 
          onClick={() => handleFilterChange('status', 'verified')}
        >
          Verified Sellers
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'pending'} 
          onClick={() => handleFilterChange('status', 'pending')}
        >
          Pending Verification
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <Typography variant="subtitle2" fontWeight={600}>
            Sort By
          </Typography>
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'created_desc'} 
          onClick={() => handleFilterChange('sortBy', 'created_desc')}
        >
          Newest First
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'created_asc'} 
          onClick={() => handleFilterChange('sortBy', 'created_asc')}
        >
          Oldest First
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'sales_desc'} 
          onClick={() => handleFilterChange('sortBy', 'sales_desc')}
        >
          Highest Sales
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'sales_asc'} 
          onClick={() => handleFilterChange('sortBy', 'sales_asc')}
        >
          Lowest Sales
        </MenuItem>
      </Menu>

      {/* Sellers Table */}
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Seller</StyledTableCell>
                  <StyledTableCell>Shop Name</StyledTableCell>
                  <StyledTableCell>Contact</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell align="right">Sales</StyledTableCell>
                  <StyledTableCell align="right">Orders</StyledTableCell>
                  <StyledTableCell align="right">Joined</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading sellers data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sellers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1">
                        No sellers found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try adjusting your search or filter to find what you're looking for
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sellers.map((seller) => (
                    <StyledTableRow 
                      key={seller.id}
                      onClick={() => handleRowClick(seller)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <SellerAvatar 
                            src={getLogoUrl(seller)} 
                            alt={seller.shop_name}
                          >
                            {seller.shop_name.charAt(0)}
                          </SellerAvatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {seller.full_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {seller.id.substring(0, 8)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {seller.shop_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {seller.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {seller.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          size="small"
                          verified={seller.admin_verified}
                          label={seller.admin_verified ? "Verified" : "Pending"}
                          icon={seller.admin_verified ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(seller.total_sale)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {seller.total_orders}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatDate(seller.created)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(seller);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Seller">
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleEditClick(e, seller)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuOpen(e, seller.id)}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalSellers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { width: 200, borderRadius: 2 }
        }}
      >
        {currentSellerId && (
          <>
            <MenuItem 
              onClick={() => {
                const seller = sellers.find(s => s.id === currentSellerId);
                handleToggleVerification(currentSellerId, seller.admin_verified);
              }}
            >
              <List>
                {sellers.find(s => s.id === currentSellerId)?.admin_verified ? (
                  <Cancel fontSize="small" color="error" />
                ) : (
                  <CheckCircle fontSize="small" color="success" />
                )}
              </List>
              <Typography variant="body2">
                {sellers.find(s => s.id === currentSellerId)?.admin_verified 
                  ? "Revoke Verification" 
                  : "Verify Seller"}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleMenuClose}>
              <List>
                <Delete fontSize="small" color="error" />
              </List>
              <Typography variant="body2">Delete Seller</Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* View Seller Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {selectedSeller && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  Seller Details
                </Typography>
                <Box>
                  <Tooltip title="Edit Seller">
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setViewModalOpen(false);
                        handleEditClick({ stopPropagation: () => {} }, selectedSeller);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    edge="end"
                    onClick={() => setViewModalOpen(false)}
                  >
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Profile" />
              <Tab label="Shop" />
              <Tab label="Stats" />
            </Tabs>
            
            <DialogContent dividers>
              {/* Profile Tab */}
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <DetailCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                          <Avatar
                            src={getLogoUrl(selectedSeller)}
                            sx={{ 
                              width: 120, 
                              height: 120, 
                              mb: 2,
                              boxShadow: 3,
                            }}
                          >
                            {selectedSeller.full_name.charAt(0)}
                          </Avatar>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            {selectedSeller.full_name}
                          </Typography>
                          <Chip
                            label={selectedSeller.admin_verified ? "Verified Seller" : "Pending Verification"}
                            color={selectedSeller.admin_verified ? "success" : "warning"}
                            size="small"
                            icon={selectedSeller.admin_verified ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                          />
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ '& > div': { mb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Username:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {selectedSeller.username}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Email:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {selectedSeller.email}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Phone:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {selectedSeller.phone}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Store color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Shop Name:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {selectedSeller.shop_name}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Joined:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {formatDate(selectedSeller.created)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday color="action" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Last Updated:
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {formatDate(selectedSeller.updated)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            color={selectedSeller.admin_verified ? "error" : "success"}
                            onClick={() => {
                              handleToggleVerification(selectedSeller.id, selectedSeller.admin_verified);
                              setSelectedSeller({
                                ...selectedSeller,
                                admin_verified: !selectedSeller.admin_verified
                              });
                            }}
                            startIcon={selectedSeller.admin_verified ? <Cancel /> : <CheckCircle />}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            {selectedSeller.admin_verified ? "Revoke Verification" : "Verify Seller"}
                          </Button>
                        </Box>
                      </CardContent>
                    </DetailCard>
                  </Grid>
                  
                  <Grid item xs={12} md={8}>
                    <DetailCard>
                      <CardContent>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Seller Information
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Full Name
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {selectedSeller.full_name}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Email Address
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {selectedSeller.email}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Phone Number
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {selectedSeller.phone}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Username
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {selectedSeller.username}
                            </Typography>
                          </Grid>
                        </Grid>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Social Media
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                          <Tooltip title="Visit Shop Website">
                            <IconButton 
                              component="a" 
                              href={selectedSeller.social} 
                              target="_blank"
                              sx={{ 
                                bgcolor: blue[50], 
                                color: blue[700],
                                '&:hover': { bgcolor: blue[100] }
                              }}
                            >
                              <LinkIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Facebook">
                            <IconButton 
                              sx={{ 
                                bgcolor: blue[50], 
                                color: blue[700],
                                '&:hover': { bgcolor: blue[100] }
                              }}
                            >
                              <Facebook />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Instagram">
                            <IconButton 
                              sx={{ 
                                bgcolor: '#FCE4EC', 
                                color: '#E91E63',
                                '&:hover': { bgcolor: '#F8BBD0' }
                              }}
                            >
                              <Instagram />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Twitter">
                            <IconButton 
                              sx={{ 
                                bgcolor: '#E1F5FE', 
                                color: '#03A9F4',
                                '&:hover': { bgcolor: '#B3E5FC' }
                              }}
                            >
                              <Twitter />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Account Status
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Paper
                              variant="outlined"
                              sx={{ p: 2, borderRadius: 2 }}
                            >
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Admin Verification
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selectedSeller.admin_verified ? (
                                  <CheckCircle fontSize="small" color="success" />
                                ) : (
                                  <Cancel fontSize="small" color="warning" />
                                )}
                                <Typography variant="body1" fontWeight={500}>
                                  {selectedSeller.admin_verified ? "Verified" : "Pending"}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Paper
                              variant="outlined"
                              sx={{ p: 2, borderRadius: 2 }}
                            >
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Email Verification
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selectedSeller.verified ? (
                                  <CheckCircle fontSize="small" color="success" />
                                ) : (
                                  <Cancel fontSize="small" color="warning" />
                                )}
                                <Typography variant="body1" fontWeight={500}>
                                  {selectedSeller.verified ? "Verified" : "Not Verified"}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </DetailCard>
                  </Grid>
                </Grid>
              )}
              
              {/* Shop Tab */}
              {tabValue === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <DetailCard>
                      <ShopBanner
                        sx={{ 
                          backgroundImage: `url(${getBannerUrl(selectedSeller)})`,
                        }}
                      />
                      <ShopLogoWrapper>
                        <ShopLogo
                          src={getLogoUrl(selectedSeller)}
                          alt={selectedSeller.shop_name}
                        >
                          {selectedSeller.shop_name.charAt(0)}
                        </ShopLogo>
                      </ShopLogoWrapper>
                      <Box sx={{ pt: 5, pb: 2, px: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h5" fontWeight={600}>
                            {selectedSeller.shop_name}
                          </Typography>
                          <Chip
                            label={selectedSeller.admin_verified ? "Verified Shop" : "Pending Verification"}
                            color={selectedSeller.admin_verified ? "success" : "warning"}
                            size="small"
                            icon={selectedSeller.admin_verified ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                          />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success" 
                          />
                          <Chip 
                            label={`${selectedSeller.total_orders} Orders`}
                            size="small"
                            variant="outlined"
                          />
                          {/* <Chip 
                            label="30+ Products"
                            size="small"
                            variant="outlined"
                          /> */}
                        </Box>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ p: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Shop Description
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {selectedSeller.shop_description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                          <Button
                            variant="outlined"
                            startIcon={<Visibility />}
                            sx={{ textTransform: 'none' }}
                          >
                            View Products
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<ShoppingCart />}
                            sx={{ textTransform: 'none' }}
                          >
                            View Orders
                          </Button>
                        </Box>
                      </Box>
                    </DetailCard>
                  </Grid>
                </Grid>
              )}
              
              {/* Stats Tab */}
              {tabValue === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <DetailCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <ShoppingCart color="primary" />
                          <Typography variant="h6" fontWeight={600}>
                            Orders
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700}>
                          {selectedSeller.total_orders}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total processed orders
                        </Typography>
                      </CardContent>
                    </DetailCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <DetailCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <AttachMoney color="success" />
                          <Typography variant="h6" fontWeight={600}>
                            Sales
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700}>
                          {formatCurrency(selectedSeller.total_sale)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total sales amount
                        </Typography>
                      </CardContent>
                    </DetailCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <DetailCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <MonetizationOn color="warning" />
                          <Typography variant="h6" fontWeight={600}>
                            Commission
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700}>
                          {formatCurrency(selectedSeller.total_commission)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Platform commission earned
                        </Typography>
                      </CardContent>
                    </DetailCard>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <DetailCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <MonetizationOn color="info" />
                          <Typography variant="h6" fontWeight={600}>
                            Balance
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700}>
                          {formatCurrency(selectedSeller.total_balance)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current seller balance
                        </Typography>
                      </CardContent>
                    </DetailCard>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Performance Overview
                      </Typography>
                      <Typography variant="body2" paragraph>
                        This seller has processed {selectedSeller.total_orders} orders with total sales of {formatCurrency(selectedSeller.total_sale)}.
                        The platform has earned {formatCurrency(selectedSeller.total_commission)} in commission.
                        The seller currently has a balance of {formatCurrency(selectedSeller.total_balance)}.
                      </Typography>
                      
                      {/* <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Sales charts and detailed analytics will be available in the next update.
                        </Typography>
                      </Alert> */}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={() => setViewModalOpen(false)} 
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                Close
              </Button>
              <Button 
                variant="contained"
                onClick={() => {
                  setViewModalOpen(false);
                  handleEditClick({ stopPropagation: () => {} }, selectedSeller);
                }}
                startIcon={<Edit />}
                sx={{ textTransform: 'none' }}
              >
                Edit Seller
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Seller Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {selectedSeller && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  Edit Seller: {selectedSeller.full_name}
                </Typography>
                <IconButton
                  edge="end"
                  onClick={() => setEditModalOpen(false)}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Profile" />
              <Tab label="Shop" />
              <Tab label="Verification" />
            </Tabs>
            
            <DialogContent dividers>
              {/* Profile Tab */}
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box
                        component="label"
                        htmlFor="logo-upload"
                        sx={{
                          display: 'inline-block',
                          position: 'relative',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="file"
                          id="logo-upload"
                          style={{ display: 'none' }}
                          accept="image/*"
                          onChange={handleLogoUpload}
                        />
                        <Avatar
                          src={logoPreview}
                          sx={{
                            width: 150,
                            height: 150,
                            margin: '0 auto 16px',
                            boxShadow: 3,
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                        >
                          {selectedSeller.full_name.charAt(0)}
                        </Avatar>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 16,
                            right: 0,
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 2,
                          }}
                        >
                          <Edit fontSize="small" />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Click to upload a new profile image
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Account Information
                      </Typography>
                      
                      <Box sx={{ bgcolor: grey[100], p: 2, borderRadius: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Seller ID
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedSeller.id}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Joined
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(selectedSeller.created)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            Last Updated
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(selectedSeller.updated)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Personal Information
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          name="full_name"
                          value={editForm.full_name}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          value={editForm.email}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={editForm.phone}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Social Media
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Shop Website/Social Link"
                      name="social"
                      value={editForm.social}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="https://example.com"
                      sx={{ mb: 3 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Additional social media integration options will be available in the next update.
                    </Typography> */}
                  </Grid>
                </Grid>
              )}
              
              {/* Shop Tab */}
              {tabValue === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Shop Banner
                    </Typography>
                    
                    <Box
                      component="label"
                      htmlFor="banner-upload"
                      sx={{
                        display: 'block',
                        position: 'relative',
                        cursor: 'pointer',
                        mb: 3,
                      }}
                    >
                      <input
                        type="file"
                        id="banner-upload"
                        style={{ display: 'none' }}
                        accept="image/*"
                        onChange={handleBannerUpload}
                      />
                      {bannerPreview ? (
                        <Box
                          sx={{
                            height: 200,
                            width: '100%',
                            backgroundImage: `url(${bannerPreview})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderRadius: 2,
                            '&:hover': {
                              opacity: 0.9,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 10,
                              right: 10,
                              bgcolor: 'white',
                              color: 'primary.main',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: 2,
                            }}
                          >
                                                        <Edit fontSize="small" />
                          </Box>
                        </Box>
                      ) : (
                        <StyledImageUpload>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Upload fontSize="large" color="action" />
                            <Typography variant="body1" sx={{ mt: 1 }}>
                              Click to upload shop banner
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Recommended size: 1200x300px
                            </Typography>
                          </Box>
                        </StyledImageUpload>
                      )}
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Shop Information
                        </Typography>
                        
                        <TextField
                          fullWidth
                          label="Shop Name"
                          name="shop_name"
                          value={editForm.shop_name}
                          onChange={handleInputChange}
                          variant="outlined"
                          sx={{ mb: 3 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Store color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        
                        <TextField
                          fullWidth
                          label="Shop Description"
                          name="shop_description"
                          value={editForm.shop_description}
                          onChange={handleInputChange}
                          variant="outlined"
                          multiline
                          rows={4}
                          placeholder="Tell customers about your shop..."
                        />
                      </Grid>
                      
                      {/* <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Shop Policies
                        </Typography>
                        
                        <Alert severity="info" sx={{ mb: 3 }}>
                          <Typography variant="body2">
                            Shop policies (return, shipping, etc.) will be configurable in the next update.
                          </Typography>
                        </Alert>
                        
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Shop Categories
                        </Typography>
                        
                        <Alert severity="info" sx={{ mb: 3 }}>
                          <Typography variant="body2">
                            Product categories and shop categorization coming soon.
                          </Typography>
                        </Alert>
                      </Grid> */}
                    </Grid>
                  </Grid>
                </Grid>
              )}
              
              {/* Verification Tab */}
              {tabValue === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Seller Verification Status
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editForm.admin_verified}
                            onChange={handleInputChange}
                            name="admin_verified"
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              Admin Verified
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {editForm.admin_verified
                                ? "This seller is verified by the admin team"
                                : "This seller is not yet verified by the admin team"}
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    </Paper>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Verified sellers get a badge on their profile and may appear higher in search results.
                    </Typography>
                    
                    {/* <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Verification Documents
                    </Typography>
                    
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        Document upload and verification system will be implemented in the next update.
                      </Typography>
                    </Alert> */}
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Verification History
                    </Typography>
                    
                    <Box sx={{ bgcolor: grey[100], p: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Current Status
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {editForm.admin_verified ? "Verified" : "Not Verified"}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Last Updated
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {currentDateTime}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          Updated By
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {currentUser}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
              <Button 
                onClick={() => setEditModalOpen(false)} 
                variant="outlined"
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained"
                onClick={handleSubmit}
                startIcon={<Save />}
                disabled={formSubmitting}
                sx={{ textTransform: 'none' }}
              >
                {formSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>



     {/* Export Dialog */}
<Dialog
  open={exportDialogOpen}
  onClose={() => setExportDialogOpen(false)}
  fullWidth
  maxWidth="sm"
  PaperProps={{
    sx: { borderRadius: 2 }
  }}
>
  <DialogTitle>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="h6" fontWeight={600}>
        Export Sellers Data
      </Typography>
      <IconButton
        edge="end"
        onClick={() => setExportDialogOpen(false)}
      >
        <Close />
      </IconButton>
    </Box>
  </DialogTitle>
  <DialogContent dividers>
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
      Select Columns to Export
    </Typography>
    <MUIList dense>
      {Object.entries(columnsToExport).map(([key, value]) => {
        let label;
        switch(key) {
          case 'seller': label = 'Seller Name'; break;
          case 'shop_name': label = 'Shop Name'; break;
          case 'contact': label = 'Contact Information (Email, Phone)'; break;
          case 'status': label = 'Verification Status'; break;
          case 'sales': label = 'Total Sales'; break;
          case 'orders': label = 'Orders'; break;
          case 'joined': label = 'Joined Date'; break;
          default: label = key;
        }
        
        return (
          <ListItem key={key} dense button onClick={() => handleToggleColumn(key)}>
            <Checkbox
              edge="start"
              checked={value}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={label} />
          </ListItem>
        );
      })}
    </MUIList>
    
    <Divider sx={{ my: 2 }} />
    
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
      Select Rows to Export
    </Typography>
    
    <FormControlLabel
      control={
        <Checkbox
          checked={selectAllRows}
          onChange={handleToggleAllRows}
          color="primary"
        />
      }
      label="Export all rows"
    />
    
    {!selectAllRows && (
      <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
        <MUIList dense>
          {sellers.map((seller, index) => (
            <ListItem key={seller.id} dense button onClick={() => handleToggleRow(index)}>
              <Checkbox
                edge="start"
                checked={selectedRows.includes(index)}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText 
                primary={seller.full_name} 
                secondary={seller.shop_name}
              />
            </ListItem>
          ))}
        </MUIList>
      </Box>
    )}
  </DialogContent>
  <DialogActions sx={{ p: 2 }}>
    <Button 
      onClick={() => setExportDialogOpen(false)} 
      variant="outlined"
      sx={{ textTransform: 'none' }}
    >
      Cancel
    </Button>
    <Button 
      variant="contained"
      onClick={exportData}
      startIcon={<Download />}
      sx={{ textTransform: 'none' }}
    >
      Export
    </Button>
  </DialogActions>
</Dialog>



    </Box>
  );
};

export default Sellers;